
import React from 'react';
import { SubscriptionPlan } from '../../../types';
import PlanFeatures from './PlanFeatures';
import { Button } from '../../../components/ui/Button';

interface PlanCardProps {
    plan: SubscriptionPlan;
    billingCycle: 'monthly' | 'annually';
    /** Plano marcado como atual na org (trial local ou pago). */
    isCurrentPlan?: boolean;
    /**
     * Assinatura Stripe cobrável (tem stripe_subscription_id).
     * Trial local NÃO conta — senão o CTA do plano atual fica travado.
     */
    hasPaidSubscription?: boolean;
    onSelectPlan: (plan: SubscriptionPlan) => void;
}

/** CTA do card: trial local pode assinar o plano atual; só Stripe pago trava "Plano Atual". */
export function resolvePlanCardAction(params: {
    isCurrentPlan: boolean;
    hasPaidSubscription: boolean;
}): { label: string; disabled: boolean } {
    const { isCurrentPlan, hasPaidSubscription } = params;

    if (hasPaidSubscription && isCurrentPlan) {
        return { label: 'Plano Atual', disabled: true };
    }

    if (hasPaidSubscription) {
        return { label: 'Mudar plano', disabled: false };
    }

    return { label: 'Assinar', disabled: false };
}

const PlanCard: React.FC<PlanCardProps> = ({
    plan,
    billingCycle,
    isCurrentPlan = false,
    hasPaidSubscription = false,
    onSelectPlan,
}) => {
    const isRecommended = plan.isRecommended && !isCurrentPlan;
    const hasBadge = isCurrentPlan || isRecommended;
    const action = resolvePlanCardAction({ isCurrentPlan, hasPaidSubscription });

    const priceStr = plan.price ?? '0';
    const monthlyPrice = typeof priceStr === 'number'
        ? priceStr
        : parseFloat(String(priceStr).replace(',', '.')) || 0;

    const priceValue = billingCycle === 'annually'
        ? monthlyPrice * 10
        : monthlyPrice;

    const formattedPrice = new Intl.NumberFormat('pt-BR', {
        style: 'decimal',
        minimumFractionDigits: priceValue % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
    }).format(priceValue);

    const cardClasses = isCurrentPlan
        ? 'max-md:border-default max-md:ring-0 max-md:shadow-sm border-success/60 ring-2 ring-success/5 shadow-md'
        : isRecommended
            ? 'max-md:border-default max-md:ring-0 max-md:shadow-sm border-primary/80 ring-2 ring-primary/10 shadow-lg hover:shadow-xl hover:-translate-y-1 lg:scale-[1.02] z-raised'
            : 'border-default shadow-sm hover:shadow-md hover:-translate-y-0.5';

    return (
        <article
            className={`relative flex flex-col max-md:gap-3 md:gap-6 w-full min-w-0 bg-card text-text max-md:rounded-xl md:rounded-2xl border border-default max-md:p-4 md:p-8 transition-base ${cardClasses} ${hasBadge ? 'max-md:pt-6 md:pt-9' : ''}`}
            aria-label={`Plano ${plan.name}`}
        >
            {hasBadge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-raised">
                    {isCurrentPlan && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider bg-success text-white border border-success whitespace-nowrap shadow-sm">
                            {hasPaidSubscription ? 'Plano Atual' : 'Em teste'}
                        </span>
                    )}
                    {isRecommended && (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider bg-primary text-primary-foreground whitespace-nowrap shadow-sm">
                            Recomendado
                        </span>
                    )}
                </div>
            )}

            <div className="flex max-md:flex-row max-md:items-start max-md:justify-between max-md:gap-3 flex-col gap-2 min-w-0">
                <header className="flex flex-col gap-1 min-w-0 flex-1">
                    <h3 className="text-heading-sm text-text max-md:leading-tight">
                        {plan.name}
                    </h3>
                    <p className="text-body-sm text-secondary leading-snug max-md:line-clamp-1 md:line-clamp-2">
                        {plan.description}
                    </p>
                </header>
                <p className="flex flex-col max-md:items-end shrink-0 md:flex-row md:flex-wrap md:items-baseline gap-x-1 gap-y-0.5">
                    <span className="text-heading-md md:text-heading-xl font-extrabold tracking-tight text-text whitespace-nowrap">
                        R$ {formattedPrice}
                    </span>
                    <span className="text-caption font-semibold text-muted max-md:text-right">
                        /{billingCycle === 'monthly' ? 'mês' : 'ano'}
                        <span className="max-md:hidden"> por usuário</span>
                    </span>
                </p>
            </div>

            <PlanFeatures features={plan.features} />

            <footer className="flex flex-col gap-1.5 md:gap-2.5 mt-auto max-md:pt-1 max-md:border-t max-md:border-subtle">
                <Button
                    className={`w-full justify-center rounded-xl font-semibold transition-base max-md:py-3 md:py-3.5 ${
                        action.disabled
                            ? 'bg-secondary text-secondary cursor-not-allowed'
                            : 'bg-primary hover:bg-primary-dark text-primary-foreground shadow-md hover:shadow-brand cursor-pointer'
                    }`}
                    size="lg"
                    onClick={() => onSelectPlan(plan)}
                    disabled={action.disabled}
                >
                    {action.label}
                </Button>
            </footer>
        </article>
    );
};

export default PlanCard;
